// App-level building blocks on top of the design system: headers, banners, empty states, menus, dialogs, fields.
import {
  useCallback, useEffect, useId, useLayoutEffect, useRef, useState, type ChangeEvent, type InputHTMLAttributes, type KeyboardEvent, type ReactNode,
  type SelectHTMLAttributes, type TextareaHTMLAttributes,
} from 'react';
import { createPortal } from 'react-dom';
import { cx } from '../ui/ds.tsx';
import { Icon, type IconName } from '../ui/Icon.tsx';

export function ScreenHeader({ title, subtitle, children, back }: { title: ReactNode; subtitle?: ReactNode; children?: ReactNode; back?: ReactNode }) {
  return (
    <header className="screen-head">
      <div className="screen-head__titles">
        {back}
        <h1>{title}</h1>
        {subtitle ? <p>{subtitle}</p> : null}
      </div>
      {children ? <div className="screen-head__tools">{children}</div> : null}
    </header>
  );
}

export function SectionHeader({ title, caption, children }: { title: ReactNode; caption?: ReactNode; children?: ReactNode }) {
  return (
    <div className="sec-head">
      <h2>{title}</h2>
      {caption || children ? <span className="sec-head__side">{caption ? <small>{caption}</small> : null}{children}</span> : null}
    </div>
  );
}

export function Banner({ tone = 'neutral', icon = 'alert', children, actions, onDismiss, dismissLabel }: {
  tone?: 'neutral' | 'waiting' | 'danger'; icon?: IconName; children: ReactNode; actions?: ReactNode; onDismiss?: () => void; dismissLabel?: string;
}) {
  return (
    <div className={`banner banner--${tone}`} role={tone === 'danger' ? 'alert' : 'status'}>
      <Icon name={icon} size={16} strokeWidth={2} />
      <div className="banner__text">{children}</div>
      {actions ? <div className="banner__actions">{actions}</div> : null}
      {onDismiss ? (
        <button type="button" className="banner__close" onClick={onDismiss} aria-label={dismissLabel} title={dismissLabel}><Icon name="close" size={14} /></button>
      ) : null}
    </div>
  );
}

export function EmptyState({ icon, title, message, children, card = true }: { icon: IconName; title: string; message: string; children?: ReactNode; card?: boolean }) {
  return (
    <div className={cx('empty', card && 'tl-card')}>
      <span className="empty__icon"><Icon name={icon} size={22} /></span>
      <div className="empty__title">{title}</div>
      <p className="empty__message">{message}</p>
      {children ? <div className="empty__actions">{children}</div> : null}
    </div>
  );
}

export function Loading({ label }: { label: string }) {
  return <div className="loading" role="status" aria-live="polite"><span className="loading__bar" /><span className="tl-sr">{label}</span></div>;
}

export function Figure({ label, value, strong, dot }: { label: string; value: string; strong?: boolean; dot?: string }) {
  return (
    <div className="figure">
      <div className="figure__label">{dot ? <span className="tl-dot" style={{ background: `var(--${dot})` }} /> : null}{label}</div>
      <div className={cx('figure__value', strong && 'is-strong')}>{value}</div>
    </div>
  );
}

// MARK: Menus (glass popovers)

export interface MenuHandle {
  close(): void;
}

/**
 * A button that opens a glass popover under it. Escape and a click outside close it; arrow keys move between
 * the menu's items.
 */
export function Menu({ label, trigger, children, align = 'start', className }: {
  label: string;
  trigger: (props: { onClick: () => void; 'aria-expanded': boolean; 'aria-haspopup': 'menu' }) => ReactNode;
  children: (menu: MenuHandle) => ReactNode;
  align?: 'start' | 'end';
  className?: string;
}) {
  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState<{ top: number; left: number; minWidth: number } | null>(null);
  const anchor = useRef<HTMLSpanElement>(null);
  const panel = useRef<HTMLDivElement | null>(null);
  const close = useCallback(() => {
    setOpen(false);
    anchor.current?.querySelector<HTMLButtonElement>('button')?.focus({ preventScroll: true });
  }, []);
  const mountPanel = useCallback((node: HTMLDivElement | null) => {
    panel.current = node;
    const first = node?.querySelector<HTMLElement>('[role="menuitem"]:not(:disabled), [role="menuitemcheckbox"]:not(:disabled)');
    (first ?? node)?.focus({ preventScroll: true });
  }, []);
  useLayoutEffect(() => {
    if (!open || !anchor.current) return;
    const r = anchor.current.getBoundingClientRect();
    const width = Math.max(220, r.width);
    const left = align === 'end' ? Math.max(8, r.right - width) : Math.min(r.left, window.innerWidth - width - 8);
    const top = r.bottom + 6;
    setPosition({ top, left: Math.max(8, left), minWidth: width });
  }, [open, align]);
  useLayoutEffect(() => {
    // Flip above the button when the menu would run off the bottom of the window.
    if (!open || !panel.current || !anchor.current || !position) return;
    const r = anchor.current.getBoundingClientRect();
    const h = panel.current.offsetHeight;
    if (position.top + h > window.innerHeight - 8 && r.top - h - 6 > 8) setPosition({ ...position, top: r.top - h - 6 });
  }, [open, position]);
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (panel.current?.contains(e.target as Node) || anchor.current?.contains(e.target as Node)) return;
      setOpen(false);
    };
    const onKey = (e: globalThis.KeyboardEvent) => {
      if (e.key === 'Escape') { e.preventDefault(); close(); }
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open, close]);
  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Tab') { close(); return; }
    if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
    e.preventDefault();
    const items = [...(panel.current?.querySelectorAll<HTMLElement>('[role="menuitem"]:not(:disabled), [role="menuitemcheckbox"]:not(:disabled)') ?? [])];
    if (items.length === 0) return;
    const index = items.indexOf(document.activeElement as HTMLElement);
    const next = items[(index + (e.key === 'ArrowDown' ? 1 : -1) + items.length) % items.length];
    next?.focus();
  };
  return (
    <span ref={anchor} className={cx('menu-anchor', className)}>
      {trigger({ onClick: () => setOpen((o) => !o), 'aria-expanded': open, 'aria-haspopup': 'menu' })}
      {open && position ? createPortal(
        <div ref={mountPanel} className="menu tl-glass tl-glass--strong" role="menu" tabIndex={-1} aria-label={label} onKeyDown={onKeyDown}
          style={{ top: position.top, left: position.left, minWidth: position.minWidth }}>
          {children({ close })}
        </div>, document.body) : null}
    </span>
  );
}

export function MenuItem({ children, onSelect, checked, danger, disabled, icon }: {
  children: ReactNode; onSelect: () => void; checked?: boolean; danger?: boolean; disabled?: boolean; icon?: IconName;
}) {
  return (
    <button type="button" role={checked === undefined ? 'menuitem' : 'menuitemcheckbox'} tabIndex={-1} aria-checked={checked} disabled={disabled}
      className={cx('menu__item', danger && 'is-danger')} onClick={onSelect}>
      <span className="menu__check">{checked ? <Icon name="check" size={14} strokeWidth={2.2} /> : icon ? <Icon name={icon} size={14} /> : null}</span>
      <span className="menu__label">{children}</span>
    </button>
  );
}

export function MenuLabel({ children }: { children: ReactNode }) {
  return <div className="menu__section tl-label">{children}</div>;
}

export function MenuDivider() {
  return <div className="menu__divider" role="separator" />;
}

// MARK: Dialogs

/** A modal sheet on `surface` over the scrim (a bottom sheet on phones). Escape closes it. */
export function Sheet({ title, subtitle, onClose, children, footer, width = 560, label }: {
  title: ReactNode; subtitle?: ReactNode; onClose: () => void; children: ReactNode; footer?: ReactNode; width?: number; label?: string;
}) {
  const ref = useRef<HTMLDialogElement>(null);
  const titleId = useId();
  useEffect(() => {
    const dialog = ref.current;
    if (!dialog) return;
    if (!dialog.open) {
      dialog.showModal();
      // The browser focuses the first focusable element; for sheets without a field that is a footer button.
      const focused = document.activeElement;
      if (!(focused instanceof HTMLInputElement || focused instanceof HTMLTextAreaElement || focused instanceof HTMLSelectElement)) dialog.focus();
    }
    const onCancel = (e: Event) => {
      e.preventDefault();
      onClose();
    };
    dialog.addEventListener('cancel', onCancel);
    return () => dialog.removeEventListener('cancel', onCancel);
  }, [onClose]);
  return createPortal(
    <dialog ref={ref} className="sheet" tabIndex={-1} aria-labelledby={titleId} aria-label={label} style={{ maxWidth: width }}
      onMouseDown={(e) => { if (e.target === ref.current) onClose(); }}>
      <div className="sheet__body">
        <div className="sheet__head">
          <div>
            <h2 id={titleId}>{title}</h2>
            {subtitle ? <p>{subtitle}</p> : null}
          </div>
        </div>
        <div className="sheet__content">{children}</div>
        {footer ? <div className="sheet__foot">{footer}</div> : null}
      </div>
    </dialog>,
    document.body,
  );
}

export function Confirm({ title, message, confirm, cancel, danger, onConfirm, onClose }: {
  title: string; message: string; confirm: string; cancel: string; danger?: boolean; onConfirm: () => void; onClose: () => void;
}) {
  return (
    <Sheet title={title} onClose={onClose} width={440}
      footer={<>
        <span className="spacer" />
        <button type="button" className="tl-btn tl-btn--plain tl-btn--md" onClick={onClose}>{cancel}</button>
        <button type="button" className={cx('tl-btn', 'tl-btn--md', danger ? 'tl-btn--danger' : 'tl-btn--primary')} onClick={() => { onConfirm(); onClose(); }}>{confirm}</button>
      </>}>
      <p className="sheet__text">{message}</p>
    </Sheet>
  );
}

// MARK: Fields

export function Field({ label, hint, children, error, inline }: { label: string; hint?: ReactNode; children: ReactNode; error?: ReactNode; inline?: boolean }) {
  return (
    <label className={cx('field', inline && 'field--inline')}>
      <span className="field__label">{label}</span>
      <span className="field__control">{children}</span>
      {hint ? <span className="field__hint">{hint}</span> : null}
      {error ? <span className="field__error">{error}</span> : null}
    </label>
  );
}

export function TextInput(props: InputHTMLAttributes<HTMLInputElement>) {
  return <input {...props} className={cx('input', props.className)} />;
}

export function TextArea(props: TextareaHTMLAttributes<HTMLTextAreaElement>) {
  return <textarea {...props} className={cx('input', 'input--area', props.className)} />;
}

export function Select({ children, ...props }: SelectHTMLAttributes<HTMLSelectElement>) {
  return (
    <span className="select">
      <select {...props} className={cx('input', 'select__control', props.className)}>{children}</select>
      <Icon name="chevronDown" size={14} className="select__chevron" />
    </span>
  );
}

export function Toggle({ checked, onChange, label, disabled }: { checked: boolean; onChange: (value: boolean) => void; label: string; disabled?: boolean }) {
  return (
    <button type="button" role="switch" aria-checked={checked} aria-label={label} disabled={disabled} className={cx('switch', checked && 'is-on')}
      onClick={() => onChange(!checked)}>
      <span className="switch__thumb" />
    </button>
  );
}

export function onValue(set: (value: string) => void) {
  return (e: ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => set(e.target.value);
}

/** Saves a file the browser offers for download (CSV, JSON). */
export function download(name: string, data: Blob | string, type = 'text/csv;charset=utf-8'): void {
  const blob = typeof data === 'string' ? new Blob([data], { type }) : data;
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
